import { useState, useEffect, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { getStock } from '../../../service/AffariService';
import { InputText } from 'primereact/inputtext';

const TableProductosStocks = ({ 
    visible, 
    onCancel, 
    loading, 
    setLoading,
    idProducto
}) => {
    const [products, setProducts] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 20,
        page: 0,
        sortField: null,
        sortOrder: null,
    });

    const [searchTerm, setSearchTerm] = useState('');

    const loadData = useCallback(() => {
        if (!idProducto) return;

        setLoading(true);

        getStock(idProducto)
            .then((data) => {
                const rawStocks = Array.isArray(data?.stocks) ? data.stocks : [];

                const mapped = rawStocks.map((s) => ({
                    almacen: s.almacen,
                    articulo: s.producto?.codigo ?? '',
                    disponible: s.stock1 ?? 0,
                    fisico: s.stock2 ?? 0,
                    bajoPedido: s.stock3 ?? 0,
                    reservado: s.stock4 ?? 0,
                    previsto: s.stock5 ?? 0,
                }));

                const term = searchTerm.trim().toLowerCase();
                const filtered = term
                    ? mapped.filter((row) =>
                          [row.almacen, row.articulo].some((value) =>
                              String(value ?? '').toLowerCase().includes(term)
                          )
                      )
                    : mapped;

                const { first, rows } = lazyParams;
                const pageSlice = filtered.slice(first, first + rows);

                const sortedData = sortData(pageSlice, lazyParams.sortField, lazyParams.sortOrder);

                setProducts(sortedData);
                setTotalRecords(filtered.length);
                setLoading(false);
            })
            .catch((err) => {
                console.error("Error fetching stock:", err);
                setLoading(false);
            });
    }, [idProducto, lazyParams, searchTerm, setLoading]);

    useEffect(() => {
        if (visible) {
            const delayDebounceFn = setTimeout(() => {
                loadData();
            }, 500);

            return () => clearTimeout(delayDebounceFn);
        }
    }, [visible, lazyParams.first, lazyParams.rows, idProducto, searchTerm, loadData]);

    const sortData = (data, field, order) => {
        if (!field || !data) return data;
        
        let sortedData = [...data];
        sortedData.sort((a, b) => {
            const value1 = a[field] ?? '';
            const value2 = b[field] ?? '';

            let comparison = 0;
            if (typeof value1 === 'number' && typeof value2 === 'number') {
                comparison = value1 - value2;
            } else {
                comparison = String(value1).localeCompare(String(value2), 'es', { sensitivity: 'base' });
            }

            return (order || 1) === 1 ? comparison : -comparison;
        });
        return sortedData;
    };

    const onPage = (event) => {
        setLazyParams(prev => ({
            ...prev,
            first: event.first,
            rows: event.rows,
            page: event.page,
        }));
    };

    const onSort = (event) => {
        setLazyParams(prev => ({
            ...prev,
            sortField: event.sortField,
            sortOrder: event.sortOrder,
        }));
        
        const sortedData = sortData(products, event.sortField, event.sortOrder);
        setProducts(sortedData);
    };

    return (
        <Dialog 
            header="Stock de Productos" 
            visible={visible} 
            className="p-fluid custom-dialog"
            onHide={onCancel}
            style={{ width: '80vw' }}
        >
            <div>
                <div className="flex justify-content-end mb-3">
                    <span className="p-input-icon-left w-full md:w-6">
                        <i className="pi pi-search" />
                        <InputText 
                            type="text" 
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setLazyParams(prev => ({ ...prev, first: 0, page: 0 }));
                            }}
                            placeholder="Buscar por almacén o artículo" 
                            className="w-full"
                        />
                    </span>
                </div>
                <DataTable 
                    value={products} 
                    lazy
                    paginator 
                    first={lazyParams.first} 
                    rows={lazyParams.rows} 
                    totalRecords={totalRecords} 
                    onPage={onPage}
                    onSort={onSort}
                    sortField={lazyParams.sortField}
                    sortOrder={lazyParams.sortOrder}
                    loading={loading}
                    rowsPerPageOptions={[10, 20, 50]}
                    emptyMessage="No se encontraron stocks"
                    stripedRows
                    showGridlines
                    tableStyle={{ minWidth: "50rem" }}
                    className="p-datatable-sm border-1 surface-border border-round"
                    scrollHeight="55vh"
                    scrollable
                >
                    <Column field="almacen" header="Almacén" sortable />
                    <Column field="articulo" header="Artículo" sortable />
                    <Column field="disponible" header="Disponible" className="text-right" />
                    <Column field="fisico" header="Físico" className="text-right" />
                    <Column field="bajoPedido" header="Bajo Pedido" className="text-right" />
                    <Column field="reservado" header="Reservado" className="text-right" />
                    <Column field="previsto" header="Previsto" className="text-right" />
                </DataTable>
            </div>
        </Dialog>
    );
};

export default TableProductosStocks;
